import { createFeature, createReducer, on } from '@ngrx/store';
import { CompanyActions } from './company.actions';
import { CompanyState } from './company-state.model';

const initialState: CompanyState = {
  companies: [],
  totalCount: 0,
  currentPage: 1,
  limit: null,
  search: '',
  isLoading: false,
  pageCache: {},
  mode: 'idle',
  selectedCompany: null,
  isLoadingDetail: false,
  supplierSettings: null,
  isLoadingSupplierSettings: false,
  isSavingSupplierSettings: false,
  buyerConfigSettings: null,
  isLoadingBuyerConfigSettings: false,
  isSavingBuyerConfigSettings: false,
  isResettingBuyerConfigSettings: false,
  segmentation: null,
  isLoadingSegmentation: false,
  isSavingSegmentation: false,
  isTogglingSegmentation: false,
  isSaving: false,
  isDeleting: false,
  currencies: [],
  operatorUsers: [],
  error: null,
  isRegisteringWebhook: false,
  isUnsubscribingWebhook: false,
};

export const companyFeatureKey = 'company';

export const companyReducer = createReducer(
  initialState,
  on(CompanyActions.loadCompanies, (state, { limit }) => ({
    ...state,
    isLoading: true,
    limit: limit ?? state.limit,
  })),
  on(CompanyActions.setSearch, (state, { search }) => ({
    ...state,
    search,
    currentPage: 1,
    isLoading: true,
    pageCache: {},
  })),
  on(CompanyActions.setPage, (state, { page }) => ({
    ...state,
    currentPage: page,
    isLoading: true,
  })),
  on(
    CompanyActions.loadCompaniesSuccess,
    (state, { companies, count, page, cachedAt }) => ({
      ...state,
      companies,
      totalCount: count,
      isLoading: false,
      pageCache: {
        ...state.pageCache,
        [page]: { companies, totalCount: count, cachedAt },
      },
    }),
  ),
  on(CompanyActions.loadCompaniesFailure, (state) => ({
    ...state,
    isLoading: false,
  })),
  on(CompanyActions.invalidateCache, (state) => ({
    ...state,
    pageCache: {},
  })),

  on(CompanyActions.loadCurrenciesSuccess, (state, { currencies }) => ({
    ...state,
    currencies,
    error: null,
  })),
  on(CompanyActions.loadCurrenciesFailure, (state, { error }) => ({
    ...state,
    error,
  })),

  on(CompanyActions.startCreate, (state) => ({
    ...state,
    mode: 'create' as const,
    selectedCompany: null,
  })),
  on(CompanyActions.selectCompany, (state) => ({
    ...state,
    mode: 'edit' as const,
    selectedCompany: null,
    supplierSettings: null,
    buyerConfigSettings: null,
    segmentation: null,
    isLoadingDetail: true,
  })),
  on(CompanyActions.reloadDetail, (state) => ({
    ...state,
    isLoadingDetail: true,
  })),
  on(CompanyActions.loadDetailSuccess, (state, { detail }) => ({
    ...state,
    selectedCompany: detail,
    isLoadingDetail: false,
    error: null,
  })),
  on(CompanyActions.loadDetailFailure, (state) => ({
    ...state,
    mode: 'idle' as const,
    isLoadingDetail: false,
  })),
  on(CompanyActions.clearForm, (state) => ({
    ...state,
    mode: 'idle' as const,
    selectedCompany: null,
    supplierSettings: null,
    buyerConfigSettings: null,
    segmentation: null,
  })),

  on(CompanyActions.loadSupplierSettings, (state) => ({
    ...state,
    isLoadingSupplierSettings: true,
    error: null,
  })),
  on(CompanyActions.loadSupplierSettingsSuccess, (state, { settings }) => ({
    ...state,
    supplierSettings: settings,
    isLoadingSupplierSettings: false,
    error: null,
  })),
  on(CompanyActions.loadSupplierSettingsFailure, (state, { error }) => ({
    ...state,
    isLoadingSupplierSettings: false,
    error,
  })),

  on(CompanyActions.loadOperatorUsersSuccess, (state, { operatorUsers }) => ({
    ...state,
    operatorUsers,
  })),

  on(CompanyActions.loadBuyerConfigSettings, (state) => ({
    ...state,
    isLoadingBuyerConfigSettings: true,
    error: null,
  })),
  on(CompanyActions.loadBuyerConfigSettingsSuccess, (state, { settings }) => ({
    ...state,
    buyerConfigSettings: settings,
    isLoadingBuyerConfigSettings: false,
    error: null,
  })),
  on(CompanyActions.loadBuyerConfigSettingsFailure, (state, { error }) => ({
    ...state,
    isLoadingBuyerConfigSettings: false,
    error,
  })),

  on(CompanyActions.saveBuyerConfigSettings, (state) => ({
    ...state,
    isSavingBuyerConfigSettings: true,
    error: null,
  })),
  on(CompanyActions.saveBuyerConfigSettingsSuccess, (state) => ({
    ...state,
    isSavingBuyerConfigSettings: false,
    error: null,
  })),
  on(CompanyActions.saveBuyerConfigSettingsFailure, (state, { error }) => ({
    ...state,
    isSavingBuyerConfigSettings: false,
    error,
  })),

  on(CompanyActions.saveBuyerCountrySettings, (state) => ({
    ...state,
    isSavingBuyerConfigSettings: true,
    error: null,
  })),
  on(CompanyActions.saveBuyerCountrySettingsSuccess, (state) => ({
    ...state,
    isSavingBuyerConfigSettings: false,
    error: null,
  })),
  on(CompanyActions.saveBuyerCountrySettingsFailure, (state, { error }) => ({
    ...state,
    isSavingBuyerConfigSettings: false,
    error,
  })),

  on(CompanyActions.resetBuyerConfigSettings, (state) => ({
    ...state,
    isResettingBuyerConfigSettings: true,
    error: null,
  })),
  on(CompanyActions.resetBuyerConfigSettingsSuccess, (state) => ({
    ...state,
    isResettingBuyerConfigSettings: false,
    error: null,
  })),
  on(CompanyActions.resetBuyerConfigSettingsFailure, (state, { error }) => ({
    ...state,
    isResettingBuyerConfigSettings: false,
    error,
  })),

  on(CompanyActions.saveCompany, (state) => ({
    ...state,
    isSaving: true,
  })),
  on(CompanyActions.saveCompanySuccess, (state) => ({
    ...state,
    isSaving: false,
    mode: 'edit' as const,
  })),
  on(CompanyActions.saveCompanyFailure, (state) => ({
    ...state,
    isSaving: false,
  })),

  on(CompanyActions.updateCompany, (state) => ({
    ...state,
    isSaving: true,
  })),
  on(CompanyActions.updateCompanySuccess, (state) => ({
    ...state,
    isSaving: false,
  })),
  on(CompanyActions.updateCompanyFailure, (state) => ({
    ...state,
    isSaving: false,
  })),

  on(CompanyActions.deleteCompany, (state) => ({
    ...state,
    isDeleting: true,
  })),
  on(CompanyActions.deleteCompanySuccess, (state) => ({
    ...state,
    isDeleting: false,
    mode: 'idle' as const,
    selectedCompany: null,
  })),

  on(CompanyActions.saveSupplierSettings, (state) => ({
    ...state,
    isSavingSupplierSettings: true,
    error: null,
  })),

  on(CompanyActions.saveSupplierSettingsSuccess, (state) => ({
    ...state,
    isSavingSupplierSettings: false,
    error: null,
  })),
  on(CompanyActions.deleteCompanyFailure, (state) => ({
    ...state,
    isDeleting: false,
  })),

  on(CompanyActions.enableSegmentation, (state) => ({
    ...state,
    isTogglingSegmentation: true,
    error: null,
  })),
  on(CompanyActions.enableSegmentationSuccess, (state) => ({
    ...state,
    isTogglingSegmentation: false,
    supplierSettings: state.supplierSettings
      ? { ...state.supplierSettings, isSegmentationEnabled: true }
      : state.supplierSettings,
    error: null,
  })),
  on(CompanyActions.enableSegmentationFailure, (state, { error }) => ({
    ...state,
    isTogglingSegmentation: false,
    error,
  })),

  on(CompanyActions.disableSegmentation, (state) => ({
    ...state,
    isTogglingSegmentation: true,
    error: null,
  })),
  on(CompanyActions.disableSegmentationSuccess, (state) => ({
    ...state,
    isTogglingSegmentation: false,
    segmentation: null,
    supplierSettings: state.supplierSettings
      ? { ...state.supplierSettings, isSegmentationEnabled: false }
      : state.supplierSettings,
    error: null,
  })),
  on(CompanyActions.disableSegmentationFailure, (state, { error }) => ({
    ...state,
    isTogglingSegmentation: false,
    error,
  })),

  on(CompanyActions.loadSegmentParents, (state) => ({
    ...state,
    isLoadingSegmentation: true,
    error: null,
  })),
  on(CompanyActions.loadSegmentParentsSuccess, (state, { segmentation }) => ({
    ...state,
    segmentation,
    isLoadingSegmentation: false,
    error: null,
  })),
  on(CompanyActions.loadSegmentParentsFailure, (state, { error }) => ({
    ...state,
    isLoadingSegmentation: false,
    error,
  })),

  on(CompanyActions.saveSegmentParents, (state) => ({
    ...state,
    isSavingSegmentation: true,
    error: null,
  })),
  on(CompanyActions.saveSegmentParentsSuccess, (state) => ({
    ...state,
    isSavingSegmentation: false,
    error: null,
  })),
  on(CompanyActions.saveSegmentParentsFailure, (state, { error }) => ({
    ...state,
    isSavingSegmentation: false,
    error,
  })),

  on(CompanyActions.registerWebhook, (state) => ({
    ...state,
    isRegisteringWebhook: true,
    error: null,
  })),
  on(CompanyActions.registerWebhookSuccess, (state) => ({
    ...state,
    isRegisteringWebhook: false,
    error: null,
  })),
  on(CompanyActions.registerWebhookFailure, (state, { error }) => ({
    ...state,
    isRegisteringWebhook: false,
    error,
  })),

  on(CompanyActions.unsubscribeWebhook, (state) => ({
    ...state,
    isUnsubscribingWebhook: true,
    error: null,
  })),
  on(CompanyActions.unsubscribeWebhookSuccess, (state) => ({
    ...state,
    isUnsubscribingWebhook: false,
    error: null,
  })),
  on(CompanyActions.unsubscribeWebhookFailure, (state, { error }) => ({
    ...state,
    isUnsubscribingWebhook: false,
    error,
  })),

  on(CompanyActions.resetState, () => initialState),
);

export const companyFeature = createFeature({
  name: companyFeatureKey,
  reducer: companyReducer,
});
